import { Grid } from "@mui/material";
import type { FC } from "react";
import type { Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import CurrencyPicker from "../../../components/currencyPicker/CurrencyPicker";
import FormInfoEdit from "../../../components/formComponents/FormInfoEdit";
import FormTextField from "../../../components/formComponents/FormTextField";
import ImagesPicker from "../../../components/imagesPicker/ImagesPicker";
import GooglePlaceSelect from "../../../components/placeSelect/GooglePlaceSelect";
import { useImages } from "../../../media/useImages";
import { ProviderProductType } from "../../providers/providersApi";
import { useProviderWithId } from "../../providers/useProviderWithId";
import type { ProductResponse } from "../productsApi";
import ProviderSelect from "../ProviderSelect";
import ProductsGroup from "./ProductsGroup";

export type ProductFormState = Omit<ProductResponse, "options" | "id" | "provider"> & {
  readonly provider: { readonly id: string; readonly productId?: string; readonly useDynamicPrices: boolean };
};

interface Props {
  readonly control: Control<ProductFormState>;
  readonly providerId: string | undefined;
  readonly disabled?: boolean;
}

const ProductEdit: FC<Props> = ({ control, providerId, disabled }) => {
  const { provider } = useProviderWithId(providerId);
  const { images, isLoading } = useImages();
  const productType = provider?.productType;
  const providerTaxInfo = provider?.taxInfo;
  const useProviderTaxNo = providerTaxInfo?.useForAllProducts ?? false;
  const isHotel = productType === ProviderProductType.HOTEL || productType === ProviderProductType.HOTEL_VAT_NP;

  return (
    <Grid size={12} container spacing={2}>
      <Grid size={{ xs: 12, md: 6, lg: 5 }}>
        <FormTextField label="Nazwa" control={control} name="name" autoFocus disabled={disabled} />
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 5 }}>
        <Controller
          name="provider.id"
          control={control}
          render={({ field: { onChange, value } }) => (
            <ProviderSelect providerId={value} onChange={onChange} disabled={disabled} />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 2 }}>
        <Controller
          name="currency"
          control={control}
          render={({ field: { onChange, value } }) => (
            <CurrencyPicker label="Waluta" value={value} onChange={onChange} />
          )}
        />
      </Grid>
      {provider?.useDynamicPrices && (
        <Grid size={{ xs: 12, md: 6, lg: 5 }}>
          <FormTextField label="Id u dostawcy" control={control} name="provider.productId" autoFocus />
        </Grid>
      )}
      <Grid size={{ xs: 12, md: 4 }}>
        <FormTextField
          label="NIP"
          name="taxNo"
          disabled={disabled || useProviderTaxNo}
          shrinkInputLabel={useProviderTaxNo}
          control={control}
          valueOverride={useProviderTaxNo ? (providerTaxInfo?.taxNo ?? "") : undefined}
        />
      </Grid>
      {!provider?.useDynamicPrices && <Grid size={{ xs: 12, md: 6, lg: 5 }} />}
      <Grid size={{ xs: 12, md: 4 }}>
        <FormTextField label="Numer konta bankowego" control={control} name="bankAccount" disabled={disabled} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormTextField
          label="Email kontaktowy"
          type="email"
          control={control}
          name="contact.email"
          disabled={disabled}
        />
      </Grid>
      {isHotel && (
        <Grid size={{ xs: 12, md: 4 }}>
          <ProductsGroup name="productsGroup" control={control} />
        </Grid>
      )}
      <Grid size={{ xs: 12, md: 4 }}>
        <FormTextField
          multiline
          minRows={3}
          label="Adres"
          control={control}
          name="contact.address"
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Controller
          name="contact.googlePlaceId"
          control={control}
          render={({ field: { onChange, value } }) => <GooglePlaceSelect googlePlaceId={value} onChange={onChange} />}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
        <FormInfoEdit label="Info" name="info.external" control={control} disabled={disabled} />
      </Grid>
      <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
        <FormInfoEdit label="Info prywatne" name="info.internal" control={control} disabled={disabled} />
      </Grid>
      <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
        <FormInfoEdit label="Opis WWW" name="description.polish" control={control} disabled={disabled} />
      </Grid>
      <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
        <FormInfoEdit label="Opis WWW (eng)" name="description.english" control={control} disabled={disabled} />
      </Grid>
      <Grid size={12}>
        <Controller
          name="images"
          control={control}
          render={({ field: { onChange, value } }) => (
            <ImagesPicker
              label="Zdjęcia"
              limit={10}
              images={images}
              isLoading={isLoading}
              selectedImages={value}
              onSelectionChange={onChange}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

export default ProductEdit;
